import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetImageCountV1WorkspacesWorkspaceIdPdpsPdpIdImageCountGetData,
  type GetImageCountV1WorkspacesWorkspaceIdPdpsPdpIdImageCountGetError,
  type GetImageCountV1WorkspacesWorkspaceIdPdpsPdpIdImageCountGetResponse,
  getImageCountV1WorkspacesWorkspaceIdPdpsPdpIdImageCountGet,
} from '../../generated';

export type GetImageCountQueryVariables = GetImageCountV1WorkspacesWorkspaceIdPdpsPdpIdImageCountGetData['path'];

export type GetImageCountQuery = GetImageCountV1WorkspacesWorkspaceIdPdpsPdpIdImageCountGetResponse;

export const useGetImageCountQuery = <
  TData = GetImageCountQuery,
  TError = GetImageCountV1WorkspacesWorkspaceIdPdpsPdpIdImageCountGetError,
>(
  { workspace_id, pdp_id }: GetImageCountQueryVariables,
  options?: Omit<UseQueryOptions<GetImageCountQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetImageCountQuery, TError, TData>({
    queryKey: ['GetImageCount', { workspace_id, pdp_id }],
    queryFn: async () => {
      const { data } = await getImageCountV1WorkspacesWorkspaceIdPdpsPdpIdImageCountGet({
        path: { workspace_id, pdp_id },
        throwOnError: true,
      });
      return data as GetImageCountQuery;
    },
    ...options,
  });
};

useGetImageCountQuery.getKey = ({ workspace_id, pdp_id }: GetImageCountQueryVariables) => [
  'GetImageCount',
  { workspace_id, pdp_id },
];

useGetImageCountQuery.fetcher = (
  { workspace_id, pdp_id }: GetImageCountQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getImageCountV1WorkspacesWorkspaceIdPdpsPdpIdImageCountGet({
      path: { workspace_id, pdp_id },
      throwOnError: true,
    });
    return data as GetImageCountQuery;
  };
};

useGetImageCountQuery.schema = {
  method: 'get',
  path: '/v1/workspaces/{workspace_id}/pdps/{pdp_id}/image-count',
};
